import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import Select from "react-select";
import Switch from "react-switch";
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterSelect, SpinnerButton } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, filterHelpers, UserContext } from '../utils';

export default function PrintMachineLabels(props) {
   const { currentUser } = useContext(UserContext);
   const SELECT_OPTION = { value: 0, label: "-- All --" };
   const ALL_MACHINES = { value: 0, label: "-- Select --", activeMachines: "0" };
   const INIT_DATA = { machineId: 0, machineNumber: 0, printerId: 0, printerName: "NONE", printPdf: false };

   const [data, setData] = useState(INIT_DATA);
   const [downloading, setDownloading] = useState(false);
   const [initialized, setInitialized] = useState(false);
   const [machines, setMachines] = useState([]);
   const [machineNums, setMachineNums] = useState([]);
   const [printers, setPrinters] = useState([]);
   const [printPdf, setPrintPDF] = useState(false);
   const [selectedPrinter, setSelectedPrinter] = useState(null);
   const [filters, setFilters] = useState([
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNumber", value: SELECT_OPTION }
   ]);

   useEffect(() => {
      async function setDropdowns() {
         if (!currentUser) return;
         api
            .fetch(`Repair/LoadMachineLabelDropdowns`)
            .then(response => {
               if (response.data.success) {
                  const machineList = _.concat([ALL_MACHINES],
                    _.map(response.data.machines, m => ({ value: m.machineId, label: m.machineName, activeMachines: m.activeMachines })));
                  const printerList = _.map(response.data.printers, p => ({ value: p.id, label: p.nameOnly }));
                  return {
                    machineList: machineList,
                    printerList: printerList
                  };
               } else {
                  throw new Error(response);
               }
            })
            .then(async ({ machineList, printerList }) => {
               setMachines(machineList);
               setMachineNums([SELECT_OPTION]);
               setPrinters(printerList);
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => setInitialized(true))
      }
      setDropdowns();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser])

   useEffect(() => {
      let changes = Object.assign({}, data);
      if(selectedPrinter) {
         changes.printerId = selectedPrinter.value;
         changes.printerName = selectedPrinter.label;
      }
      else {
         changes.printerId = 0;
         changes.printerName = "NONE";
      }
      changes.printPdf = printPdf;
      setData(changes);

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedPrinter, printPdf])

   const fixMachines = async (activeMachines) => {
      // reset the selected machine number
      let changes = Object.assign({}, data);
      changes.machineNumber = 0;
      setData(changes);

      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat([SELECT_OPTION], _.map(nums, m => ({ value: parseInt(m, 10), label: m })));
      setMachineNums(newMachineList);
      return newMachineList;
   };

   const handlePrint = nextChecked => { setPrintPDF(nextChecked); }

   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, x => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, m => m.value === 0);
      let newFilters = filterHelpers.getFilters(filters, ({ filterName: "machineId", value: mach }));
      newFilters = filterHelpers.getFilters(newFilters, ({ filterName: "machineNumber", value: numFilter }));
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineId = newFilterValue.value;
      changes.machineNumber = 0;
      setData(changes);
   };

   function onMachineNumChange(newFilterValue) {
      let tmpFilter = { value: newFilterValue.value, label: newFilterValue.label };
      let newFilters = filterHelpers.getFilters(filters, { filterName: "machineNumber", value: tmpFilter });
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineNumber = newFilterValue.value;
      setData(changes);
   }

   function printReport() {
      if(data.machineId === 0) {
         toast.error("You must select a machine type");
         return;
      }      
      if(!printPdf && data.printerId === 0) {
         toast.error("You must select a printer");
         return;
      }
            
      setDownloading(true);
      if (printPdf) {
         api
            .postDownloadBinary(`Report/RunMachineLabelRpt`, data, constants.MIME_TYPES.pdf, `MachineLabels.pdf`)
            .then((res) => {
               if(res?.data === false) {
                  toast.error(res.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => { setDownloading(false); })
      }
      else {
         api
            .post(`Report/RunMachineLabelRpt`, data)
            .then(response => {
               if (response.data.success) {
                  toast.success("Machine labels were reprinted");
               }
               else {
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => { setDownloading(false); })
      }
   }

   return (
      <DesktopContainer screenName="Print Machine Labels">
         {initialized === true ? (
            <div className="filter-header">
               <Row className="justify-content-center">
                  <Col xs={10}>
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>Machine:</Col>
                        <Col xs={8}>
                           <FilterSelect
                              filterName="machineId"
                              options={machines}
                              value={filterHelpers.getValue(filters, "machineId")}
                              onChangeCallback={e => onMachineChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>Machine #:</Col>
                        <Col xs={8}>
                           <FilterSelect
                              filterName="machineNumber"
                              options={machineNums}
                              value={filterHelpers.getValue(filters, "machineNumber")}
                              onChangeCallback={e => onMachineNumChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>Printer:</Col>
                        <Col xs={8}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={printers}
                              value={selectedPrinter}
                              onChange={(e) => setSelectedPrinter(e)}
                              isClearable={true}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>{printPdf ? "Download as PDF" : "Print to printer"}</Col>
                        <Col xs={4}>
                           <Switch onChange={handlePrint} checked={printPdf} className="react-switch" />
                        </Col>
                        <Col xs={4}>
                           <SpinnerButton className="shredButton float-end" onClick={printReport} on={downloading} disabled={downloading}>
                              Print Labels
                           </SpinnerButton>
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </div>
         ) : null}
         <br />
      </DesktopContainer >
   );
}
